import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The Grade 11 topics the course reached for but had never quite said.
 *
 * Each of these existed in the year as an *implication* of something already
 * taught, which is the most expensive kind of gap: it looks covered from the
 * outside, and a student meets it for the first time in an exam.
 *
 * `radical-functions` — the course could transform any curve and had used √x as
 * an example, but never asked what its domain and range were or where it
 * starts.
 *
 * `exponential-models` — growth and decay were taught as y = a·bˣ, which is the
 * right formula and not the one a bank uses. Compounding n times a year is
 * where the exponent stops being "the number of years", and it is also the only
 * honest way to introduce e.
 *
 * `natural-logarithm` — the logarithm lesson taught log_b for a general b and
 * the calculator has two specific buttons. A student who has not been told
 * which bases those are reads `ln` as a typo.
 *
 * `tangent-function` — sine and cosine were freed from the triangle; tangent
 * was left in it. It is the one of the three that can fail to exist, which is
 * exactly why it is worth its own lesson rather than a paragraph.
 *
 * `recursive-sequences` — arithmetic and geometric sequences were given closed
 * formulas. The rule a student actually writes down first is the recursive one,
 * and the interesting sequences (Fibonacci, a loan balance) have no closed form
 * at this level at all.
 */
export const GRADE_ELEVEN_LESSONS: Lesson[] = [
  /* ------------------------------------------------------ radical functions */
  lesson({
    id: 'radical-functions-01',
    unitId: 'unit-rational-radical',
    conceptId: 'radical-functions',
    title: 'Radical Functions',
    summary: 'Curves that begin somewhere — and the one root that does not have to.',
    objectives: [
      'Find the domain and range of a square-root function',
      'Locate the point where a square-root curve starts',
      'Say why a cube-root function has no such restriction',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'A curve with an end',
        blocks: [
          text('Every curve so far has run the full width of the page. A parabola opens forever, a line goes on in both directions, an exponential flattens but never stops.'),
          math('y = \\sqrt{x}'),
          text('This one starts. At x = 0 it is at the origin, and to the left of that there is nothing at all — not a gap, not an asymptote, simply no curve, because no real number squares to a negative.'),
          insight('That makes the domain the *first* question about a radical function rather than an afterthought. For most functions the domain is everything and the exceptions are worth hunting for; here the exception is the shape of the thing.'),
        ],
      },
      intuition: {
        title: 'Where it starts, and which way it goes',
        blocks: [
          text('A square root demands that what sits under it is zero or more. So finding the domain is solving one inequality:'),
          math('y = \\sqrt{x - 4} \\quad\\Longrightarrow\\quad x - 4 \\ge 0 \\quad\\Longrightarrow\\quad x \\ge 4'),
          warning('The sign in the bracket is not the direction of the shift. `x − 4` moves the curve four to the *right*, because it is x = 4 that makes the root zero — the same rule already met on parabolas, and the same place it catches people.', 'Minus means right'),
          text('That first allowed input is where the curve begins, and it is worth naming as a point rather than as a number. For y = √(x − h) + k the starting point is (h, k), and everything else follows from it:'),
          list([
            'The curve starts at (h, k) and goes right, because the root only grows.',
            'The domain is x ≥ h — everything from the start onwards.',
            'The range is y ≥ k, unless a negative outside flips it to y ≤ k.',
            'It climbs forever, but ever more slowly: reaching y = 10 needs an input a hundred away from the start.',
          ]),
          text('The cube root is the exception that explains the rule. It has no starting point at all:'),
          math('\\sqrt[3]{-8} = -2 \\quad\\text{because}\\quad (-2)^{3} = -8'),
          insight('An odd root accepts negatives and an even root does not, and the reason is one line of arithmetic: an odd power keeps the sign of its base, while an even power destroys it. So ∛x has domain and range of every real number, and its graph runs through the origin from bottom-left to top-right.'),
          tip('Written as exponents the two are x^(1/2) and x^(1/3), which is the form that makes the difference visible: an even denominator is an even root, and an even root is where the domain closes.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'sqrt',
              transform: { h: 4, k: 1 },
              min: -2,
              max: 12,
              points: [{ x: 4, y: 1, label: 'starts at (4, 1)' }],
              label: 'y = √(x − 4) + 1',
            },
            'The curve begins at (4, 1) and travels right and up. To the left of x = 4 there is no curve, because there is nothing for the root to take.',
          ),
          text('Compare it with the plain √x, which starts at the origin. The whole picture has been carried four right and one up — the transformation rules of the last unit, applied to a curve whose starting point makes them easy to check.'),
          tip('To sketch one of these, plot the start and then one convenient point: whatever makes the root come out as 1, 4 or 9. Two points and the knowledge that it flattens are enough for a good sketch.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('State the domain and range of y = √(2x − 6) − 5, and say where the curve starts.'),
          steps([
            { label: 'The root demands a non-negative inside.', latex: '2x - 6 \\ge 0' },
            { label: 'Solve it.', latex: 'x \\ge 3', note: 'This is the domain.' },
            { label: 'At that input the root is zero.', latex: 'y = \\sqrt{0} - 5 = -5' },
            { label: 'So the curve starts there.', latex: '(3,\\ -5)' },
            { label: 'The root adds nothing below zero, so y only rises.', latex: 'y \\ge -5' },
          ]),
          text('Note that the 2 inside changed the domain but not the range. A horizontal squash moves where the curve is allowed to be; it cannot change how low it goes, because the lowest the root itself can be is still zero.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('radical-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('radical-functions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('radical-functions', 3, 'Domains with a coefficient inside, and the cube roots that have none.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A square root needs its inside to be zero or more — solve that inequality for the domain.',
            'The curve starts where the root is zero, and travels right from there.',
            'For y = √(x − h) + k the start is (h, k), the domain x ≥ h and the range y ≥ k.',
            'A minus inside shifts right, not left.',
            'An odd root — the cube root — accepts every real number, because odd powers keep their sign.',
          ]),
        ],
      },
    },
  }),

  /* ----------------------------------------------------- exponential models */
  lesson({
    id: 'exponential-models-01',
    unitId: 'unit-logarithms',
    conceptId: 'exponential-models',
    title: 'Compound Interest, Populations and Half-Lives',
    summary: 'What happens when the growth is applied more than once a year — and what it settles on when it never stops.',
    objectives: [
      'Use the compound interest formula with any number of periods a year',
      'Model a population or a decaying quantity from its rate and its starting amount',
      'Find a remaining amount from a half-life',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The same 6%, four different answers',
        blocks: [
          text('£1000 is invested at 6% a year for one year. How much is there at the end?'),
          text('It depends on something the question has not said: how often the interest is *added*. Paid once at the end of the year it is £1060. Paid in two instalments of 3%, the second instalment earns interest on the first:'),
          table(['Compounded', 'Rate each time', 'Times', 'After one year'], [
            ['yearly', '6%', '1', '£1060.00'],
            ['half-yearly', '3%', '2', '£1060.90'],
            ['monthly', '0.5%', '12', '£1061.68'],
            ['daily', '6/365 %', '365', '£1061.83'],
          ]),
          insight('The gaps are small and they are not nothing, and — more interestingly — they are getting smaller. Something is stopping this from running away, and finding out what it is produces the second most important number in mathematics.'),
        ],
      },
      intuition: {
        title: 'Dividing the rate, multiplying the count',
        blocks: [
          text('Compounding n times a year does two things at once, and both have to be done:'),
          math('A = P\\left(1 + \\frac{r}{n}\\right)^{nt}'),
          list([
            '**P** is the principal — what you started with.',
            '**r** is the annual rate as a decimal, so 6% is 0.06.',
            '**n** is how many times a year the interest is added.',
            '**t** is the number of *years*, not the number of periods.',
          ]),
          warning('Each period pays r ÷ n, and there are n × t of them. Using the full annual rate at every period is the common error and it is a large one: 6% twelve times over is 100.7% growth, not 6.2%.', 'Divide the rate, multiply the count'),
          warning('This is also not simple interest. Simple interest pays P·r·t — the same amount every year, always taken from the opening balance. Compound interest is taken from the *current* balance, which is why the formula has an exponent rather than a multiplication.', 'The interest earns interest'),
          text('The same shape models anything that grows or shrinks by a fixed proportion. A population of 5000 growing at 2% a year is 5000 × 1.02^t; a car losing 15% of its value each year is worth 18000 × 0.85^t. Only the multiplier changes.'),
          insight('A **half-life** is decay stated the other way round: instead of a rate you are told how long it takes to halve. Count the halvings — elapsed time ÷ half-life — and that count is the exponent.'),
          math('A = A_{0} \\cdot \\left(\\tfrac{1}{2}\\right)^{t / h}'),
          warning('That exponent is a count of halvings, not a divisor. 12 grams with a half-life of 3 days, after 9 days, is halved three times to 1.5 grams — it is not 12 ÷ 3.', 'Halved that many times'),
          text('Now back to the table. Push n up and the totals converge rather than explode, on a number that has no simpler name:'),
          math('\\left(1 + \\tfrac{1}{n}\\right)^{n} \\longrightarrow e = 2.71828\\ldots'),
          insight('So compounding *continuously* — every instant — is not infinite growth. It is A = Pe^{rt}, and £1000 at 6% for a year comes to £1061.84: a penny more than daily compounding and never any more than that. e is what the doubling-up settles on, which is why it turns up everywhere anything grows smoothly.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'exponential',
              min: -1,
              max: 9,
              points: [
                { x: 0, y: 1, label: 'the principal' },
                { x: 3, y: 8, label: 'three doublings' },
              ],
              label: 'growth by a fixed factor',
            },
            'Growth by a fixed factor. The curve is the same shape whatever the rate; changing the rate changes only how quickly it gets steep.',
          ),
          table(['Situation', 'Model', 'What the exponent counts'], [
            ['6% a year, yearly', 'P(1.06)^t', 'years'],
            ['6% a year, monthly', 'P(1 + 0.06/12)^(12t)', 'months'],
            ['6% a year, continuously', 'Pe^(0.06t)', 'nothing — it never stops'],
            ['half-life of 3 days', 'A₀(½)^(t/3)', 'halvings'],
          ]),
          tip('Before computing, decide what the exponent is counting. Almost every wrong answer in this topic is an exponent counting the wrong thing.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('£2500 is invested at 4% a year, compounded quarterly. What is it worth after 6 years, to the nearest penny?'),
          steps([
            { label: 'Identify the four quantities.', latex: 'P = 2500,\\ r = 0.04,\\ n = 4,\\ t = 6' },
            { label: 'Divide the rate among the periods.', latex: '\\frac{0.04}{4} = 0.01' },
            { label: 'Count the periods.', latex: '4 \\times 6 = 24' },
            { label: 'Apply the formula.', latex: 'A = 2500(1.01)^{24}' },
            { label: 'Evaluate.', latex: '\\approx £3175.62' },
          ]),
          text('Check it against the two answers either side. Simple interest would have given 2500(1 + 0.04 × 6) = £3100, and yearly compounding 2500(1.04)⁶ ≈ £3163.30. Quarterly sits just above both, which is what more frequent compounding should do — and if your answer had come out below £3100, the rate and the count would have been the place to look.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('exponential-models', 4)] },
      independent: { title: 'On your own', blocks: [practice('exponential-models', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('exponential-models', 3, 'Compounding periods, half-lives, and telling compound from simple.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A = P(1 + r/n)^(nt): divide the rate by the periods, and multiply the years by them.',
            't is measured in years however often the interest is added.',
            'Simple interest is P(1 + rt) — a fixed amount each year, and always less.',
            'Half-life decay halves the amount once per half-life: the exponent is a count of halvings.',
            'Compounding more often converges rather than runs away, on e = 2.71828…',
            'Continuous growth is A = Pe^(rt), the ceiling every compounding schedule approaches.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------ common and natural logarithms */
  lesson({
    id: 'natural-logarithm-01',
    unitId: 'unit-logarithms',
    conceptId: 'natural-logarithm',
    title: 'Common and Natural Logarithms',
    summary: 'Two bases out of infinitely many get a button of their own, and neither choice is arbitrary.',
    objectives: [
      'Read log as base 10 and ln as base e',
      'Convert between exponential and logarithmic form with base e',
      'Solve a continuous growth or decay equation using ln',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The two buttons',
        blocks: [
          text('A logarithm can have any positive base but a calculator offers exactly two, and they are not labelled with their bases at all:'),
          math('\\log x \\;=\\; \\log_{10} x \\qquad\\qquad \\ln x \\;=\\; \\log_{e} x'),
          text('Written without a base, **log** means base 10 — the **common logarithm**, because our number system is base 10 and log 1000 = 3 counts the digits. **ln** means base e — the **natural logarithm**, from the number the last lesson found hiding in continuous growth.'),
          warning('These are different functions, not two notations for one. ln 100 ≈ 4.61 while log 100 = 2, and reading one as the other is not a rounding error.', 'Two buttons, two answers'),
        ],
      },
      intuition: {
        title: 'Why e deserves a button',
        blocks: [
          text('Nothing about the definition changes. ln is a logarithm, so it is still the question "what power?", asked of an awkward base:'),
          math('\\ln x = y \\iff e^{y} = x'),
          list([
            'ln 1 = 0, because e⁰ = 1 — the same as every logarithm.',
            'ln e = 1, because e¹ = e.',
            'ln e^k = k, which is the identity that does most of the work.',
            'ln of a negative number does not exist, because e to any power is positive.',
          ]),
          insight('All three log laws survive unchanged, because they were consequences of the exponent rules and not of the base: ln(xy) = ln x + ln y, ln(x/y) = ln x − ln y, and ln(xⁿ) = n ln x.'),
          text('So why bother with an irrational base? Because continuous growth is written with e, and ln is the operation that undoes it in one step:'),
          math('A = Pe^{rt} \\;\\longrightarrow\\; \\frac{A}{P} = e^{rt} \\;\\longrightarrow\\; \\ln\\!\\left(\\frac{A}{P}\\right) = rt'),
          insight('The exponent comes down as itself, with no log e left over to divide by, because ln e = 1. That is the whole reason e is the natural base: it is the one where the undoing leaves nothing behind.'),
          warning('In A = Pe^{rt} the rate and the time are multiplied *inside* the exponent. Computing Pe^r prices one year of growth however long the investment ran.', 'The exponent is rt, not r'),
          text('And when the base is neither 10 nor e, change of base uses whichever button you like — both give the same answer, since the ratio is what matters:'),
          math('\\log_{7} 50 = \\frac{\\log 50}{\\log 7} = \\frac{\\ln 50}{\\ln 7} \\approx 2.01'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'logarithm',
              min: -3,
              max: 9,
              asymptotes: { vertical: [0] },
              points: [{ x: 1, y: 0, label: '(1, 0)' }],
              label: 'a logarithm curve',
            },
            'Every logarithm curve has this shape and passes through (1, 0). Changing the base only stretches it vertically — which is exactly what the change-of-base rule says.',
          ),
          table(['x', 'log x', 'ln x'], [
            ['1', '0', '0'],
            ['e ≈ 2.718', '0.434', '1'],
            ['10', '1', '2.303'],
            ['100', '2', '4.605'],
            ['1000', '3', '6.908'],
          ]),
          tip('Notice the second column is the third divided by 2.303, which is ln 10. The two functions are proportional, which is why either can answer any question — and why mixing them up gives an answer that is out by a constant factor rather than obviously absurd.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A culture of 300 bacteria grows continuously at 12% an hour. When does it reach 1000? Give the answer to two decimal places.'),
          steps([
            { label: 'Write the continuous model.', latex: '300e^{0.12t} = 1000' },
            { label: 'Divide by the starting amount.', latex: 'e^{0.12t} = \\frac{10}{3}' },
            { label: 'Take ln of both sides — the exponent comes straight down.', latex: '0.12t = \\ln\\!\\left(\\tfrac{10}{3}\\right)' },
            { label: 'Evaluate the logarithm.', latex: '0.12t \\approx 1.20397' },
            { label: 'Divide by the rate.', latex: 't \\approx 10.03 \\text{ hours}' },
          ]),
          insight('Taking log instead of ln would have worked too — 0.12t·log e = log(10/3) — but with an extra constant to carry. That is the practical case for the natural logarithm: not that the others are wrong, but that this one leaves nothing to tidy up.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('natural-logarithm', 4)] },
      independent: { title: 'On your own', blocks: [practice('natural-logarithm', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('natural-logarithm', 3, 'Continuous models, and telling the two bases apart.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'log with no base written means base 10; ln means base e.',
            'ln x = y and e^y = x are the same statement.',
            'ln e^k = k, and ln e = 1 — the identity that makes e the natural base.',
            'All three log laws hold for ln, unchanged.',
            'Continuous growth is A = Pe^(rt), and ln is what undoes it in one step.',
            'Change of base works with either button: log_b a = log a ÷ log b = ln a ÷ ln b.',
          ]),
        ],
      },
    },
  }),

  /* ------------------------------------------------------- tangent function */
  lesson({
    id: 'tangent-function-01',
    unitId: 'unit-trig-functions',
    conceptId: 'tangent-function',
    title: 'The Tangent Function',
    summary: 'The third ratio is a quotient, and quotients have somewhere they cannot go.',
    objectives: [
      'Evaluate tangent from the coordinates on the unit circle',
      'Say where the tangent function is undefined, and why',
      'Use the period of π rather than 2π',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The ratio that can fail',
        blocks: [
          text('Sine and cosine were freed from the triangle by reading them as coordinates on the unit circle. Tangent comes along for the ride, since it was always the third ratio:'),
          math('\\tan\\theta = \\frac{\\sin\\theta}{\\cos\\theta} = \\frac{y}{x}'),
          text('But a quotient is not like a coordinate. Sine and cosine exist for every angle there is; tangent does not.'),
          math('\\tan 90^{\\circ} = \\frac{1}{0} \\quad\\text{— undefined}'),
          insight('A calculator answers "error" here, and it is right to. This is the same event already met in rational functions: a denominator reaching zero, and a curve that runs off to infinity beside a line it never touches.'),
        ],
      },
      intuition: {
        title: 'Gaps, sign and a shorter period',
        blocks: [
          text('Everything about the tangent graph follows from reading it as a quotient of the two coordinates.'),
          list([
            '**tan θ = 0** wherever y = 0 — at 0°, 180°, 360°. A zero numerator is an ordinary crossing.',
            '**tan θ is undefined** wherever x = 0 — at 90° and 270°. A zero denominator is a vertical asymptote.',
            '**tan θ is positive** where x and y share a sign: quadrants I and III.',
            '**tan θ takes every real value**, because a quotient with a shrinking denominator is unbounded.',
          ]),
          warning('Do not put the gaps where sine is zero. It is the *bottom* of a fraction that causes trouble, and the bottom is cosine.', 'The gap is cosine\u2019s'),
          text('The period is where tangent stops resembling the other two. Go half a turn from any angle and both coordinates change sign:'),
          math('\\tan(\\theta + 180^{\\circ}) = \\frac{-y}{-x} = \\frac{y}{x} = \\tan\\theta'),
          insight('The two minus signs cancel, so tangent repeats after half a turn. Its period is 180° — π radians — not 2π. Sine and cosine need the full circle because each depends on one coordinate, whose sign is not restored until the whole way round.'),
          warning('tan θ is sin θ ÷ cos θ, not sin θ × cos θ. The product never leaves the band between −½ and ½; tangent is unbounded, so the two cannot be confused for long if you check one value.', 'Divide, do not multiply'),
          text('The special angles are worth knowing directly, and they come from the coordinates already learned:'),
          table(['θ', 'sin θ', 'cos θ', 'tan θ'], [
            ['0°', '0', '1', '0'],
            ['30°', '1/2', '√3/2', '1/√3'],
            ['45°', '√2/2', '√2/2', '1'],
            ['60°', '√3/2', '1/2', '√3'],
            ['90°', '1', '0', 'undefined'],
          ]),
          tip('At 45° the coordinates are equal, so their quotient is 1 — the one row you can reconstruct without remembering anything.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'tangent',
              min: -5,
              max: 5,
              asymptotes: { vertical: [-4.712, -1.571, 1.571, 4.712] },
              points: [{ x: 0, y: 0, label: 'tan 0 = 0' }],
              label: 'y = tan x',
            },
            'Separate branches, each climbing from far below to far above between two asymptotes. The gaps are at ±π/2 and ±3π/2, where cosine is zero.',
          ),
          text('Count the distance between two crossings and it is π, not 2π. That is the picture of the halved period: a full copy of the curve fits between consecutive asymptotes.'),
          tip('The branches never join. As with a rational function there is no way to draw this without lifting your pen, and drawing a vertical line through the gap would claim the function has a value there.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find tan 210°, and list every angle between 0° and 360° with the same tangent.'),
          steps([
            { label: 'Find the reference angle.', latex: '210^{\\circ} - 180^{\\circ} = 30^{\\circ}' },
            { label: 'In quadrant III both coordinates are negative.', latex: '\\tan 210^{\\circ} = \\frac{-1/2}{-\\sqrt{3}/2}' },
            { label: 'The signs cancel.', latex: '= \\frac{1}{\\sqrt{3}} \\approx 0.577' },
            { label: 'Tangent repeats every 180°.', latex: '210^{\\circ} - 180^{\\circ} = 30^{\\circ}' },
          ]),
          text('So tan 210° = tan 30°, and within one turn those are the only two. A sine question would have had one solution in that range rather than two, and a tangent question always has exactly two — which is a useful check that the period was used and not assumed.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('tangent-function', 4)] },
      independent: { title: 'On your own', blocks: [practice('tangent-function', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('tangent-function', 3, 'Asymptotes, quadrant signs, and the period of π.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'tan θ = sin θ ÷ cos θ = y ÷ x on the unit circle.',
            'It is zero where sine is zero, and undefined where cosine is.',
            'The asymptotes are at 90°, 270°, and every 180° from there.',
            'Its period is 180° (π), half that of sine and cosine.',
            'It is positive in quadrants I and III, where the coordinates share a sign.',
            'Its range is every real number — unlike sine and cosine, it is unbounded.',
          ]),
        ],
      },
    },
  }),

  /* ----------------------------------------------------- recursive sequences */
  lesson({
    id: 'recursive-sequences-01',
    unitId: 'unit-sequences-11',
    conceptId: 'recursive-sequences',
    title: 'Recursive Sequences',
    summary: 'Defining a term from the one before it — what that buys, and what it costs.',
    objectives: [
      'Read and apply a recursive definition',
      'Convert between a recursive rule and an explicit formula',
      'Say when a recursive definition is the only one available',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two ways to say the same sequence',
        blocks: [
          text('The sequence 5, 8, 11, 14, … can be described in two quite different ways.'),
          math('a_{n} = 3n + 2 \\qquad\\qquad a_{1} = 5,\\ \\ a_{n} = a_{n-1} + 3'),
          text('The first is **explicit**: hand it a term number and it hands back the term. The second is **recursive**: it says where to start and how to get from any term to the next one.'),
          insight('The recursive version is usually the one you notice first — "it goes up by 3" is a description of the step, not of the hundredth term. The explicit version is the one you want when someone asks for the hundredth term.'),
        ],
      },
      intuition: {
        title: 'What each form is good for',
        blocks: [
          text('A recursive definition is always two statements, and both are needed:'),
          list([
            'A **first term**, which anchors the sequence. Without it the rule "add 3" describes infinitely many sequences.',
            'A **rule** relating each term to the one before, written with aₙ₋₁ in it.',
          ]),
          warning('The rule takes the *previous term* as its input, not the term number. Substituting n into aₙ = aₙ₋₁ + 3 is reading a recursive rule as though it were explicit, and it produces a different sequence entirely.', 'The input is a term, not a position'),
          text('The cost is that you cannot skip. To find a₅₀ from a recursive rule you have to pass through the forty-nine terms before it, which is why converting to an explicit formula is worth doing when it is possible:'),
          table(['Kind', 'Recursive', 'Explicit'], [
            ['arithmetic', 'aₙ = aₙ₋₁ + d', 'aₙ = a₁ + (n − 1)d'],
            ['geometric', 'aₙ = r·aₙ₋₁', 'aₙ = a₁·r^(n−1)'],
          ]),
          insight('Reading across those rows is the conversion. The recursive rule shows the *operation* — adding gives arithmetic, multiplying gives geometric — and the explicit formula applies it n − 1 times in one go.'),
          text('It is not always possible. The Fibonacci sequence is defined from the two terms before each one:'),
          math('F_{1} = 1,\\quad F_{2} = 1,\\quad F_{n} = F_{n-1} + F_{n-2}'),
          text('1, 1, 2, 3, 5, 8, 13, 21, … — and this has no explicit formula at this level at all. Neither does a loan balance, where each month\u2019s figure is the last one plus interest minus a payment. For those the recursive form is not a first draft; it is the definition.'),
          tip('When a term depends on two earlier ones, the definition needs two starting values. In general a recursive definition needs as many seeds as it looks back.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['n', '1', '2', '3', '4', '5', '6'], [
            ['aₙ = aₙ₋₁ + 4, a₁ = 3', '3', '7', '11', '15', '19', '23'],
            ['aₙ = 2aₙ₋₁, a₁ = 3', '3', '6', '12', '24', '48', '96'],
            ['aₙ = aₙ₋₁ + aₙ₋₂, a₁ = a₂ = 1', '1', '1', '2', '3', '5', '8'],
          ]),
          text('Each row is built left to right, and none of the entries can be written before the one to its left. That is the whole character of a recursive definition, and it is why writing the terms out in a row is the reliable way to work with one.'),
          tip('Writing them in a row also stops the commonest slip. Going from a₂ to a₆ is four applications of the rule; counted off the page it is hard to get wrong, and done in your head it is easy to apply once.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A sequence has a₁ = 7 and aₙ = aₙ₋₁ − 4. Find a₆, and write an explicit formula.'),
          steps([
            { label: 'Write the terms out, applying the rule each time.', latex: '7,\\ 3,\\ -1,\\ -5,\\ -9,\\ -13' },
            { label: 'Count: a₆ is the sixth of those.', latex: 'a_{6} = -13' },
            { label: 'The rule subtracts, so it is arithmetic with d = −4.', latex: 'd = -4' },
            { label: 'Apply the arithmetic formula.', latex: 'a_{n} = 7 - 4(n - 1)' },
            { label: 'Check it against a₆.', latex: '7 - 4(5) = -13 \\quad\\checkmark' },
          ]),
          text('The check is the point of the last line. Reaching a₆ takes five steps from a₁, not six, and the explicit formula records that as the n − 1 — the same off-by-one that the sequences lesson warned about, now visible as a count of applications.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('recursive-sequences', 4)] },
      independent: { title: 'On your own', blocks: [practice('recursive-sequences', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('recursive-sequences', 3, 'Converting between the two forms, and sequences that look back twice.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A recursive definition is a first term plus a rule using the previous term.',
            'The rule\u2019s input is the term before, never the term number.',
            'Without the first term the rule describes infinitely many sequences.',
            'Reaching a distant term means passing through every term before it.',
            'Adding a constant is arithmetic; multiplying by one is geometric — and both convert to explicit formulas.',
            'Some sequences, like Fibonacci, have no explicit formula at this level, and the recursion is the definition.',
          ]),
        ],
      },
    },
  }),
];
